import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from './useAuth'

interface RequirePermissionProps {
  /** Single permission the caller must hold. */
  permission: string
  children: ReactNode
  /** Where to send unauthorized users. Defaults to the in-app forbidden page. */
  redirectTo?: string
}

/**
 * Gate a route behind a permission code. The API enforces the same check, so
 * this only keeps unauthorized controls out of the UI.
 */
export function RequirePermission({ permission, children, redirectTo = '/forbidden' }: RequirePermissionProps) {
  const { hasPermission } = useAuth()
  if (!hasPermission(permission)) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}

interface CanProps {
  permission?: string
  /** Render when the caller holds at least one of these codes. */
  anyOf?: string[]
  children: ReactNode
  /** Rendered when the caller is not allowed. Defaults to nothing. */
  fallback?: ReactNode
}

/** Conditionally render UI based on the caller's permissions. */
export function Can({ permission, anyOf, children, fallback = null }: CanProps) {
  const { hasPermission, hasAnyPermission } = useAuth()
  const allowed = permission ? hasPermission(permission) : anyOf ? hasAnyPermission(anyOf) : true
  return <>{allowed ? children : fallback}</>
}
